<template>
  <component
    :is="displayAsLink ? 'a' : 'div'"
    :id="`product-variant-selector-item-${index || value}`"
    :class="[
      'product-variant-selector-item',
      {
        'product-variant-selector-item--checked': currentVariantSelected,
        'product-variant-selector-item--new': newStyle,
      },
    ]"
    :href="variantSelectorHref"
    :tabindex="tabindex"
    @keydown.enter="$emit('input', item.url)"
    @keydown.space.prevent="$emit('input', item.url)"
    @click.prevent="$emit('input', item.url)"
  >
    <fn-radio-button
      :label="item.name"
      :radio-value="item.url"
      :value="value"
      @update:value="$emit('input', $event)"
    >
      <div class="product-variant-selector-item-label">
        <div
          class="pvsil-title"
          data-hj-allow
        >
          {{ item.name }}
        </div>

        <div class="pvsil-pricing">
          <fn-product-pricing
            :pricing="item.pricing"
            :stock-level-status="item.stockLevelStatus"
            product-variant
            @change-tab="(tab) => $emit('changeTab', tab)"
          />
        </div>

        <div
          v-if="item.highestSavings"
          class="pvsil-badge"
        >
          <fn-tag
            v-if="enableSavingsBadge"
            hint
          >
            {{ $t('product.badges.highestSavings') }}</fn-tag
          >
        </div>
      </div>
    </fn-radio-button>
  </component>
</template>

<script>
export default defineComponent({
  props: {
    item: {
      type: Object,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
    tabindex: {
      type: Number,
      default: 0,
    },
    index: {
      type: Number,
      default: null,
    },
    newStyle: {
      type: Boolean,
    },
    displayAsLink: {
      type: Boolean,
    },
  },
  emits: ['input', 'changeTab'],
  computed: {
    ...mapState(useMainStore, {
      features: 'getFeature',
    }),

    enableSavingsBadge() {
      return this.features('enableSavingsBadge');
    },

    variantSelectorHref() {
      return this.currentVariantSelected ? undefined : `${this.$host}${this.item.url}`;
    },

    currentVariantSelected() {
      return this.value === this.item.url;
    },
  },
});
</script>

<style lang="scss" scoped>
.product-variant-selector-item {
  position: relative;
  display: block;
  padding: math.div($pad, 2) $pad $pad - 4px;
  transition:
    background-color 0.1s ease-in,
    border 0.1s;
  cursor: pointer;
  border: 1px solid transparent;
  color: palette(black);
  text-decoration: none;
  width: 100%;

  &:focus,
  &.product-variant-selector-item--checked {
    background-color: palette(sand, light);
    border-color: palette(grey, light);
    border-radius: map-get($border-radiuses, 'l');
    outline: 0;
  }

  &.product-variant-selector-item--new {
    border: none;
    box-shadow: inset 0 0 0 1px palette(grey, light);
    text-decoration: none;
    border-radius: map-get($border-radiuses, 'xl');
    margin-bottom: math.div($pad, 2);
    background-color: $color-general-background-neutral-default;

    &:last-child {
      margin-bottom: 0;
    }

    &:focus,
    &.product-variant-selector-item--checked {
      background-color: $color-general-background-neutral-default;
      border: none;
      border-radius: map-get($border-radiuses, 'xl');
      box-shadow: inset 0 0 0 3px palette(orientation, green);
    }
  }

  .choices & {
    &:focus,
    &.product-variant-selector-item--checked {
      background-color: transparent;
      border-color: transparent;
      border-radius: 0;
    }
  }

  :deep(.radio-button .radio-button-label) {
    max-width: calc(100% - #{2 * $pad});
  }
}

.product-variant-selector-item-label {
  display: flex;
  flex-direction: column;
  margin: 0 (-(math.div($grid-margin-width, 2)));

  .pvsil-title {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: span-columns(4);
    margin: 0 math.div($grid-margin-width, 2);
    font-size: map-get($font-sizes, 'l');
    font-weight: bold;
    word-break: break-word;
    white-space: normal;
  }

  .pvsil-pricing {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: span-columns(8);
    margin: 0 math.div($grid-margin-width, 2);
  }

  .pvsil-badge {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    right: -$pad;
    top: 0;
    bottom: 0;
  }
}
</style>
